import { useCallback, useEffect, useState } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiRequest, loginRequest } from '../../authConfig';
import { fetchMyOrganization } from '../../flux/actions/organizationThunks';
import { fetchCurrentUser } from '../../flux/actions/userThunks';
import * as InvitationApi from '../../flux/facades/invitationApi';
import { useAppDispatch } from '../../flux/stores/hooks';
import { useApiAccessToken } from '../../utils/useApiAccessToken';

const errorStatus = (error) => error?.status;
const isInteractionRequired = (error) => error instanceof InteractionRequiredAuthError;

function errorMessageForStatus(status) {
    if (status === 404) return 'This invitation link is invalid or unavailable.';
    if (status === 410) return 'This invitation has expired or has already been used.';
    if (status === 429) return 'Too many requests. Please try again later.';
    return 'We could not load this invitation. Please try again.';
}

/**
 * The invitation token intentionally stays in the React Router parameter and
 * request URL only. Do not add it to Redux, console output, or user-visible
 * diagnostics.
 */
export const InvitationPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { instance } = useMsal();
    const { getAccessToken } = useApiAccessToken();
    const [view, setView] = useState('loading');
    const [invitation, setInvitation] = useState(null);
    const [message, setMessage] = useState(null);
    const [isRedeeming, setIsRedeeming] = useState(false);
    const [confirmDecline, setConfirmDecline] = useState(false);

    const beginAuthentication = useCallback(async () => {
        const returnUrl = window.location.href;
        try {
            await instance.loginRedirect({ ...loginRequest, redirectStartPage: returnUrl });
        } catch {
            setView('error');
            setMessage('Unable to start sign-in. Please try again.');
        }
    }, [instance]);

    const restartAuthentication = useCallback(async () => {
        const account = instance.getActiveAccount();
        const returnUrl = window.location.href;
        setView('signing-in');
        setMessage(null);
        try {
            if (account) {
                await instance.acquireTokenRedirect({ ...apiRequest, account, redirectStartPage: returnUrl });
            } else {
                await instance.loginRedirect({ ...loginRequest, redirectStartPage: returnUrl });
            }
        } catch {
            setView('error');
            setMessage('Your session needs to be renewed. Please try again.');
        }
    }, [instance]);

    const loadInvitation = useCallback(async () => {
        if (!token) {
            setView('unavailable');
            setMessage(errorMessageForStatus(404));
            return;
        }

        setView('loading');
        setMessage(null);
        try {
            // Invite links have one controlled token flow. Do not begin an
            // automatic redirect here; offer a user-triggered recovery screen.
            const accessToken = await getAccessToken({ redirectOnInteractionRequired: false });
            if (!accessToken) return;
            const detail = await InvitationApi.getInvitation(accessToken, token);
            setInvitation(detail);
            setView('ready');
        } catch (error) {
            const status = errorStatus(error);
            if (isInteractionRequired(error) || status === 401) {
                setView('auth-required');
                setMessage('We could not verify your session. Sign in again, then we will retry this invitation.');
            } else if (status === 404) {
                setView('unavailable');
                setMessage(errorMessageForStatus(status));
            } else if (status === 410) {
                setView('expired');
                setMessage(errorMessageForStatus(status));
            } else if (status === 429) {
                setView('rate-limited');
                setMessage(errorMessageForStatus(status));
            } else {
                setView('error');
                setMessage(errorMessageForStatus(status));
            }
        }
    }, [getAccessToken, token]);

    useEffect(() => {
        if (!token) {
            setView('unavailable');
            setMessage(errorMessageForStatus(404));
            return;
        }
        // MSAL has already restored the account before React's
        // useIsAuthenticated state necessarily catches up after a redirect.
        // The active account is what the API-token flow actually needs.
        if (!instance.getActiveAccount()) {
            setView('signing-in');
            beginAuthentication();
            return;
        }
        loadInvitation();
    }, [beginAuthentication, instance, loadInvitation, token]);

    const handleAccept = async () => {
        if (!token) return;
        setIsRedeeming(true);
        setMessage(null);
        try {
            const accessToken = await getAccessToken({ redirectOnInteractionRequired: false });
            if (!accessToken) return;
            await InvitationApi.acceptInvitation(accessToken, token);
            await dispatch(fetchCurrentUser({ accessToken }));
            await dispatch(fetchMyOrganization({ accessToken }));
            navigate('/organization', { replace: true });
        } catch (error) {
            const status = errorStatus(error);
            if (isInteractionRequired(error) || status === 401) {
                setView('auth-required');
                setMessage('We could not verify your session. Sign in again, then we will retry this invitation.');
            } else if (status === 404) {
                setView('unavailable');
                setMessage(errorMessageForStatus(status));
            } else if (status === 410) {
                setView('expired');
                setMessage(errorMessageForStatus(status));
            } else if (status === 429) {
                setView('rate-limited');
                setMessage(errorMessageForStatus(status));
            } else {
                setMessage('We could not accept this invitation. Please try again.');
            }
        } finally {
            setIsRedeeming(false);
        }
    };

    const handleDecline = async () => {
        if (!token) return;
        setIsRedeeming(true);
        setMessage(null);
        try {
            const accessToken = await getAccessToken({ redirectOnInteractionRequired: false });
            if (!accessToken) return;
            await InvitationApi.declineInvitation(accessToken, token);
            setConfirmDecline(false);
            setView('declined');
        } catch (error) {
            const status = errorStatus(error);
            if (isInteractionRequired(error) || status === 401) {
                setView('auth-required');
                setMessage('We could not verify your session. Sign in again, then we will retry this invitation.');
            } else if (status === 404) {
                setView('unavailable');
                setMessage(errorMessageForStatus(status));
            } else if (status === 410) {
                setView('expired');
                setMessage(errorMessageForStatus(status));
            } else if (status === 429) {
                setView('rate-limited');
                setMessage(errorMessageForStatus(status));
            } else {
                setMessage('We could not decline this invitation. Please try again.');
            }
        } finally {
            setIsRedeeming(false);
        }
    };

    if (view === 'signing-in') {
        return <div className="page-content invitation-page"><p className="projects-loading">Taking you to sign in…</p></div>;
    }

    if (view === 'declined') {
        return <div className="page-content invitation-page"><div className="pw-container"><section className="pw-section invitation-card"><h1 className="pw-page-title">Invitation declined</h1><p>You have declined this organization invitation.</p><Link className="rules-btn-primary" to="/">Return to home</Link></section></div></div>;
    }

    if (view === 'auth-required') {
        return <div className="page-content invitation-page"><div className="pw-container"><section className="pw-section invitation-card"><h1 className="pw-page-title">Sign-in required</h1><p role="alert">{message}</p><button type="button" className="rules-btn-primary" onClick={restartAuthentication}>Sign in again</button><Link className="rules-btn-ghost" to="/">Return to home</Link></section></div></div>;
    }

    if (view === 'unavailable' || view === 'expired' || view === 'rate-limited' || view === 'error') {
        const canRetry = view === 'rate-limited' || view === 'error';
        return <div className="page-content invitation-page"><div className="pw-container"><section className="pw-section invitation-card"><h1 className="pw-page-title">Invitation unavailable</h1><p role="alert">{message}</p>{canRetry && <button type="button" className="rules-btn-primary" onClick={loadInvitation}>Retry</button>}<Link className="rules-btn-ghost" to="/">Return to home</Link></section></div></div>;
    }

    if (view === 'loading' || !invitation) {
        return <div className="page-content invitation-page"><p className="projects-loading">Loading invitation…</p></div>;
    }

    return (
        <div className="page-content invitation-page">
            <div className="pw-container">
                <section className="pw-section invitation-card" aria-labelledby="invitation-title">
                    <h1 id="invitation-title" className="pw-page-title">Join {invitation.organization_name}</h1>
                    <p>You have been invited to join this organization.</p>
                    <dl className="invitation-details">
                        <div><dt>Organization</dt><dd>{invitation.organization_name}</dd></div>
                        <div><dt>Role</dt><dd>{invitation.role}</dd></div>
                        <div><dt>Invited email</dt><dd>{invitation.invited_email}</dd></div>
                        <div><dt>Expires</dt><dd>{new Date(invitation.expires_at).toLocaleString()}</dd></div>
                    </dl>
                    {invitation.email_mismatch && <p className="members-admin-warning" role="note">This invitation was sent to {invitation.invited_email}, but you are signed in with a different email address. You may still accept it if this is expected.</p>}
                    {message && <p className="org-error" role="alert">{message}</p>}
                    <div className="pw-actions">
                        <button type="button" className="rules-btn-primary" onClick={handleAccept} disabled={isRedeeming}>{isRedeeming ? 'Working…' : 'Accept invitation'}</button>
                        <button type="button" className="rules-btn-ghost" onClick={() => setConfirmDecline(true)} disabled={isRedeeming}>Decline invitation</button>
                    </div>
                </section>
            </div>
            {confirmDecline && <div className="members-modal-backdrop" role="presentation"><div className="members-modal" role="dialog" aria-modal="true" aria-labelledby="decline-invitation-title"><h2 id="decline-invitation-title">Decline this invitation?</h2><p>Declining consumes the invitation and cannot be undone.</p><div className="pw-actions"><button type="button" className="rules-btn-ghost" disabled={isRedeeming} onClick={() => setConfirmDecline(false)}>Cancel</button><button type="button" className="members-remove-button" disabled={isRedeeming} onClick={handleDecline}>{isRedeeming ? 'Declining…' : 'Decline invitation'}</button></div></div></div>}
        </div>
    );
};

export default InvitationPage;
